import { CalendarEvent } from '../services/calendarService';
import { RECRUITING_PERIOD_BG, recruitingPeriodRestrictiveness } from './recruitingCalendar';

export type CalendarDisplayEvent = {
    id: string;
    title: string;
    start: string;
    end?: string;
    allDay?: boolean;
    display?: 'background';
    backgroundColor?: string;
    classNames?: string[];
    extendedProps: {
        source: string;
        description?: string;
        recruitingEventType?: string | null;
        color?: string | null;
        webViewLink?: string | null;
    };
};

function startOfUtcDay(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * FullCalendar dateClick uses local midnight, which is the previous UTC day in BST/CEST.
 * Prefer the cell's YYYY-MM-DD so single-day dead periods still match.
 */
export function utcDateFromCalendarClick(date: Date, dateStr?: string): Date {
    const isoDay = dateStr?.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (isoDay) {
        return new Date(`${isoDay}T12:00:00.000Z`);
    }
    return date;
}

export function isDateWithinRecruitingEvent(event: CalendarEvent, date: Date): boolean {
    const day = startOfUtcDay(date);
    const eventStart = startOfUtcDay(new Date(event.start));
    if (Number.isNaN(eventStart.getTime())) {
        return false;
    }

    if (!event.end) {
        return day.getTime() === eventStart.getTime();
    }

    const exclusiveEnd = startOfUtcDay(new Date(event.end));
    if (Number.isNaN(exclusiveEnd.getTime())) {
        return day.getTime() === eventStart.getTime();
    }

    const inclusiveEnd = new Date(exclusiveEnd);
    inclusiveEnd.setUTCDate(inclusiveEnd.getUTCDate() - 1);
    return day.getTime() >= eventStart.getTime() && day.getTime() <= inclusiveEnd.getTime();
}

export function findRecruitingEventsOnDate(events: CalendarEvent[], date: Date): CalendarEvent[] {
    return events.filter((event) => isDateWithinRecruitingEvent(event, date));
}

function compareByStartLaterFirst(a: CalendarEvent, b: CalendarEvent): number {
    return new Date(b.start).getTime() - new Date(a.start).getTime();
}

/** Most restrictive period first so a day click opens the exception, not the broader window. */
export function compareMostRestrictiveRecruitingFirst(a: CalendarEvent, b: CalendarEvent): number {
    const byRank =
        recruitingPeriodRestrictiveness(a.recruitingEventType)
        - recruitingPeriodRestrictiveness(b.recruitingEventType);
    if (byRank !== 0) {
        return byRank;
    }
    return compareByStartLaterFirst(a, b);
}

/** Least restrictive first so FullCalendar paints the exception period on top. */
export function compareLeastRestrictiveRecruitingFirst(a: CalendarEvent, b: CalendarEvent): number {
    return -compareMostRestrictiveRecruitingFirst(a, b);
}

export function selectRecruitingEventForDate(
    events: CalendarEvent[],
    date: Date
): CalendarEvent | undefined {
    const matches = findRecruitingEventsOnDate(events, date);
    if (matches.length === 0) {
        return undefined;
    }
    return [...matches].sort(compareMostRestrictiveRecruitingFirst)[0];
}

export function toRecruitingBackgroundEvents(events: CalendarEvent[]): CalendarDisplayEvent[] {
    return [...events].sort(compareLeastRestrictiveRecruitingFirst).map((event) => {
        const slug = event.recruitingEventType ?? '';
        return {
            id: `${event.id}-bg`,
            title: '',
            start: event.start,
            end: event.end,
            allDay: true,
            display: 'background' as const,
            backgroundColor: RECRUITING_PERIOD_BG[slug] ?? '#f1f5f9',
            classNames: ['recruiting-bg', slug ? `recruiting-bg--${slug}` : ''].filter(Boolean),
            extendedProps: {
                source: 'recruiting',
                description: event.description ?? '',
                recruitingEventType: event.recruitingEventType ?? null,
                color: event.color ?? null,
            },
        };
    });
}

export function mapStandardCalendarEvents(events: CalendarEvent[]): CalendarDisplayEvent[] {
    return events
        .filter((event) => event.source?.toLowerCase() !== 'recruiting')
        .map((event) => ({
            id: event.id,
            title: event.title,
            start: event.start,
            end: event.end,
            allDay: event.allDay,
            extendedProps: {
                source: event.source?.toLowerCase() ?? '',
                description: event.description ?? '',
                webViewLink: event.webViewLink ?? null,
                recruitingEventType: event.recruitingEventType ?? null,
                color: event.color ?? null,
            },
        }));
}

export function buildCalendarDisplayEvents(
    filteredEvents: CalendarEvent[],
    recruitingEvents: CalendarEvent[],
    showRecruiting: boolean
): CalendarDisplayEvent[] {
    const standardEvents = mapStandardCalendarEvents(filteredEvents);
    if (!showRecruiting || recruitingEvents.length === 0) {
        return standardEvents;
    }

    return [...standardEvents, ...toRecruitingBackgroundEvents(recruitingEvents)];
}

export function apiEventFromCalendarClick(event: {
    id: string;
    title: string;
    startStr: string;
    endStr: string;
    allDay: boolean;
    extendedProps?: {
        source?: string;
        description?: string | null;
    };
}): CalendarEvent {
    const source = event.extendedProps?.source ?? 'recruiting';
    const normalizedSource = source.toLowerCase();
    return {
        id: event.id,
        title: event.title,
        start: event.startStr,
        end: event.endStr,
        allDay: event.allDay,
        source: normalizedSource as CalendarEvent['source'],
        description: event.extendedProps?.description ?? null,
        readOnly:
            normalizedSource === 'company'
            || normalizedSource === 'team'
            || normalizedSource === 'recruiting',
    };
}
